/**
 * Ukeplanlegger — the weekly meal-planner panel (Slice 3 of the meal-planner
 * feature). Desktop: a collapsible right-hand sidebar (docks under the sticky
 * TopBar via z-index, not document flow — see `styles/app.css`). Mobile: a
 * floating "Ukeplan" button opens a full-screen overlay. Both presentations
 * share the same day-list rendering; only the chrome around it differs.
 *
 * Role-gating (UI mirror only — the API + RLS are authoritative): a
 * read/comment member sees the plan but gets no servings stepper / remove
 * affordance, and this component never issues a write call for them.
 *
 * Drag-and-drop (Slice 4): each day (`PlannerDay` below) is a dnd-kit
 * `useDroppable` target keyed by `dayDropId(day.date)` — the id a dropped
 * recipe card resolves against in `HomePage`'s shared `DndContext` (this
 * component doesn't own that context; `useDroppable`/`useDndContext` read
 * whichever `<DndContext>` ancestor is mounted, or fall back to dnd-kit's
 * inert defaults when there is none — e.g. this component's OWN unit tests,
 * which render it standalone and are unaffected). `addFromDrop` (exposed via
 * `ref` — see `MealPlannerPanelHandle`) is the centralized "add" call `Slice
 * 4`'s drop handler AND (in principle) any future add-by-drop caller should
 * use, mirroring `handleServings`/`handleRemove`'s optimistic-update +
 * rollback + `onSessionExpired` shape so all three mutation paths behave
 * identically.
 *
 * COLLAPSED-PANEL DECISION (desktop): a collapsed sidebar renders no
 * `planner-day` elements at all, so no droppables are registered — a drop
 * during that state simply can't happen (dnd-kit has nothing to collide
 * with). This is deliberate: the alternative (keeping hidden droppables
 * alive while collapsed) would let a user drop onto an invisible target,
 * which is worse UX than "no visible destination, no drop".
 *
 * Seams left for later slices (do not remove):
 *  - "Lag handleliste" is wired to `onCreateShoppingList` (a no-op unless the
 *    caller supplies one) — Slice 5 mounts the real shopping-list overlay.
 */

import { type CookbookRole, type PlannedMeal, roleCanEditRecipes } from "@tjommi/shared";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import {
  UnauthorizedError,
  addPlannedMeal,
  deletePlannedMeal,
  fetchMealPlanWeek,
  updatePlannedMealServings,
} from "../api";
import {
  dayDropId,
  dayDropStateClass,
  recipeIdFromDragId,
  type MealPlanDropRecipe,
} from "../lib/mealPlanDrag";
import { getWeek, type WeekDay } from "../lib/week";

/** Imperative handle (Slice 4) — lets `HomePage`'s drop handler add a planned
 * meal without lifting this component's meal state up (it stays internal, so
 * the existing fetch/optimistic-update machinery is reused as-is). */
export interface MealPlannerPanelHandle {
  /** Add `recipe` to `plannedDate` (optimistic; rolls back + surfaces
   * `planner-mutation-error` on failure, same as the servings stepper /
   * remove button). A no-op for a read/comment caller — defense in depth;
   * the caller (HomePage) never wires a drag handle for one in the first
   * place, so this should be unreachable in practice. */
  addFromDrop: (recipe: MealPlanDropRecipe, plannedDate: string) => void;
  /**
   * Re-fetch the currently viewed week. For callers that persist a meal
   * THEMSELVES (the add-to-calendar dialog and the mobile "+" sheet both POST
   * directly) — without this the panel keeps rendering the week it last
   * fetched, so a just-added meal appears to have vanished. Deliberately not
   * `addFromDrop`: that would POST a second copy.
   */
  refresh: () => void;
}

const MIN_SERVINGS = 1;
const MAX_SERVINGS = 100;

type LoadState =
  | { status: "loading" }
  | { status: "ok"; meals: PlannedMeal[] }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

function storageKeyFor(userId: string | undefined): string | null {
  return userId ? `desjeff.mealPlannerOpen.${userId}` : null;
}

function readStoredOpen(storageKey: string | null): boolean {
  if (!storageKey) return true;
  try {
    const stored = localStorage.getItem(storageKey);
    return stored === null ? true : stored === "1";
  } catch {
    return true;
  }
}

export const MealPlannerPanel = forwardRef<
  MealPlannerPanelHandle,
  {
    cookbookId: string;
    role: CookbookRole;
    accessToken: string;
    /** The caller's subject — scopes the remembered sidebar open/closed preference per user/device. */
    userId?: string;
    onSessionExpired: () => void;
    /**
     * "Lag handleliste" seam for Slice 5 (the shopping-list overlay). Receives
     * that week's Monday (`weekStart`). Omitted ⇒ the button is still shown
     * (so the affordance is discoverable) but does nothing yet.
     */
    onCreateShoppingList?: (weekStart: string) => void;
  }
>(function MealPlannerPanel(
  { cookbookId, role, accessToken, userId, onSessionExpired, onCreateShoppingList },
  ref,
): React.JSX.Element {
  const canEdit = roleCanEditRecipes(role);
  const storageKey = storageKeyFor(userId);

  const [weekOffset, setWeekOffset] = useState(0);
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpenState] = useState<boolean>(() => readStoredOpen(storageKey));
  const [mobileOpen, setMobileOpen] = useState(false);

  const week = useMemo(() => getWeek(weekOffset), [weekOffset]);

  function setSidebarOpen(open: boolean): void {
    setSidebarOpenState(open);
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, open ? "1" : "0");
      } catch {
        // Best-effort — the toggle still works for this session.
      }
    }
  }

  // Switching the active cookbook returns to "this week" — a stale
  // far-future/past week from the previous cookbook would be confusing.
  useEffect(() => {
    setWeekOffset(0);
  }, [cookbookId]);

  // Bumped by `refresh()` on the imperative handle to force a re-fetch of the
  // CURRENTLY viewed week — used when a meal was added by someone other than
  // this panel (the add-to-calendar dialog / mobile "+" sheet, which POST on
  // their own and would otherwise leave the panel showing stale data).
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let ignore = false;
    setState({ status: "loading" });
    setMutationError(null);

    fetchMealPlanWeek(cookbookId, week.monday, accessToken)
      .then((meals) => {
        if (!ignore) setState({ status: "ok", meals });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) {
          setState({ status: "unauthorized" });
        } else {
          setState({
            status: "error",
            message: err instanceof Error ? err.message : "Ukjent feil",
          });
        }
      });

    return () => {
      ignore = true;
    };
    // Deliberately depends on `week.monday` (a primitive), not the `week`
    // object itself — `week` is a fresh useMemo result on every weekOffset
    // change, but only its `monday` value is used by this fetch.
    // `reloadToken` lets an outside caller (see `refresh` on the handle) force
    // a re-fetch without changing the cookbook or the viewed week.
  }, [cookbookId, week.monday, accessToken, reloadToken]);

  const mealsByDate = useMemo(() => {
    const map = new Map<string, PlannedMeal[]>();
    if (state.status !== "ok") return map;
    for (const meal of state.meals) {
      const list = map.get(meal.plannedDate) ?? [];
      list.push(meal);
      map.set(meal.plannedDate, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.position - b.position);
    return map;
  }, [state]);

  function patchLocalMeal(id: string, patch: Partial<PlannedMeal>): void {
    setState((prev) =>
      prev.status === "ok"
        ? { status: "ok", meals: prev.meals.map((m) => (m.id === id ? { ...m, ...patch } : m)) }
        : prev,
    );
  }

  function removeLocalMeal(id: string): void {
    setState((prev) =>
      prev.status === "ok" ? { status: "ok", meals: prev.meals.filter((m) => m.id !== id) } : prev,
    );
  }

  function restoreLocalMeal(meal: PlannedMeal): void {
    setState((prev) =>
      prev.status === "ok" && !prev.meals.some((m) => m.id === meal.id)
        ? { status: "ok", meals: [...prev.meals, meal] }
        : prev,
    );
  }

  /**
   * The centralized "add" call (Slice 4) — used by the drag-and-drop drop
   * handler below (exposed via `ref`), and shaped identically to
   * `handleServings`/`handleRemove`: optimistic local insert, POST WITHOUT
   * `servings` (the API defaults it to the recipe's own base servings — see
   * `CreatePlannedMealInput`), reconcile with the server's real entity on
   * success, roll back + surface `planner-mutation-error` on failure. The
   * optimistic entry's own servings mirrors what the API is about to default
   * to, so there's no visible jump when the real response lands.
   */
  async function handleAddFromDrop(recipe: MealPlanDropRecipe, plannedDate: string): Promise<void> {
    if (!canEdit) return;
    const tempId = `temp-${crypto.randomUUID()}`;
    const optimisticMeal: PlannedMeal = {
      id: tempId,
      plannedDate,
      recipeId: recipe.id,
      recipeTitle: recipe.title,
      recipeServings: recipe.servings,
      heroImageUrl: recipe.heroImageUrl ?? null,
      servings: Math.max(MIN_SERVINGS, recipe.servings ?? 1),
      position: mealsByDate.get(plannedDate)?.length ?? 0,
    };
    setState((prev) =>
      prev.status === "ok" ? { status: "ok", meals: [...prev.meals, optimisticMeal] } : prev,
    );
    setMutationError(null);

    try {
      const created = await addPlannedMeal(
        cookbookId,
        { recipeId: recipe.id, plannedDate },
        accessToken,
      );
      patchLocalMeal(tempId, created);
    } catch (err) {
      removeLocalMeal(tempId);
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke legge til i ukeplanen. Prøv igjen.");
    }
  }

  // No deps array: `handleAddFromDrop` is a fresh closure every render (not
  // wrapped in `useCallback`), so the handle is simply rebuilt every render
  // too — cheap, and guarantees `HomePage`'s ref call always sees the
  // current cookbook/token/role/week rather than a stale memoized closure.
  useImperativeHandle(ref, () => ({
    addFromDrop: (recipe, plannedDate) => void handleAddFromDrop(recipe, plannedDate),
    refresh: () => setReloadToken((t) => t + 1),
  }));

  async function handleServings(meal: PlannedMeal, delta: number): Promise<void> {
    if (!canEdit) return;
    const next = Math.min(MAX_SERVINGS, Math.max(MIN_SERVINGS, meal.servings + delta));
    if (next === meal.servings) return;

    const prevServings = meal.servings;
    patchLocalMeal(meal.id, { servings: next }); // optimistic

    try {
      const updated = await updatePlannedMealServings(meal.id, next, accessToken);
      patchLocalMeal(meal.id, updated);
    } catch (err) {
      patchLocalMeal(meal.id, { servings: prevServings }); // rollback
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke oppdatere porsjoner. Prøv igjen.");
    }
  }

  async function handleRemove(meal: PlannedMeal): Promise<void> {
    if (!canEdit) return;
    removeLocalMeal(meal.id); // optimistic

    try {
      await deletePlannedMeal(meal.id, accessToken);
    } catch (err) {
      restoreLocalMeal(meal); // rollback
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setMutationError("Kunne ikke fjerne måltidet. Prøv igjen.");
    }
  }

  function renderMeal(meal: PlannedMeal): React.JSX.Element {
    return (
      <div className="planner-meal" key={meal.id} data-testid={`planner-meal-${meal.id}`}>
        <div className="planner-meal-row">
          {meal.heroImageUrl && (
            <img
              src={meal.heroImageUrl}
              alt=""
              className="planner-meal-thumb"
              data-testid={`planner-meal-thumb-${meal.id}`}
            />
          )}
          <span className="planner-meal-title" data-testid={`planner-meal-title-${meal.id}`}>
            {meal.recipeTitle}
          </span>
          {canEdit && (
            <button
              type="button"
              className="planner-meal-remove"
              data-testid={`planner-meal-remove-${meal.id}`}
              aria-label={`Fjern ${meal.recipeTitle}`}
              onClick={() => void handleRemove(meal)}
            >
              ×
            </button>
          )}
        </div>
        {canEdit ? (
          <div className="planner-meal-servings">
            <button
              type="button"
              className="planner-stepper-btn"
              data-testid={`planner-meal-dec-${meal.id}`}
              aria-label="Færre porsjoner"
              onClick={() => void handleServings(meal, -1)}
            >
              −
            </button>
            <span data-testid={`planner-meal-servings-${meal.id}`}>{meal.servings} porsjoner</span>
            <button
              type="button"
              className="planner-stepper-btn"
              data-testid={`planner-meal-inc-${meal.id}`}
              aria-label="Flere porsjoner"
              onClick={() => void handleServings(meal, 1)}
            >
              +
            </button>
          </div>
        ) : (
          <div
            className="planner-meal-servings-readonly"
            data-testid={`planner-meal-servings-${meal.id}`}
          >
            {meal.servings} porsjoner
          </div>
        )}
      </div>
    );
  }

  // Slice 4: whether a recipe-card drag is currently in progress anywhere in
  // the shared `DndContext` (mounted by `HomePage`) — drives the "all valid
  // days get a dashed outline" state on every `PlannerDay` below. Reads
  // whichever `<DndContext>` ancestor is mounted, or dnd-kit's inert default
  // (`active: null`) when there is none — e.g. this component's own unit
  // tests, which render it standalone.
  const { active: dndActive } = useDndContext();
  const dragging = dndActive != null && recipeIdFromDragId(String(dndActive.id)) != null;

  function renderDay(day: WeekDay): React.JSX.Element {
    return (
      <PlannerDay
        key={day.date}
        day={day}
        meals={mealsByDate.get(day.date) ?? []}
        canEdit={canEdit}
        dragging={dragging}
        renderMeal={renderMeal}
      />
    );
  }

  function renderBody(): React.JSX.Element {
    if (state.status === "loading") {
      return (
        <p className="planner-state" data-testid="planner-loading">
          Laster ukeplan …
        </p>
      );
    }
    if (state.status === "unauthorized") {
      return (
        <div className="planner-state" data-testid="planner-unauthorized">
          <p>Økten er utløpt.</p>
          <button type="button" className="btn btn-primary" onClick={onSessionExpired}>
            Logg inn igjen
          </button>
        </div>
      );
    }
    if (state.status === "error") {
      return (
        <p className="planner-state" data-testid="planner-error-state" role="alert">
          Kunne ikke laste ukeplanen.
        </p>
      );
    }
    return (
      <div className="planner-days" data-testid="planner-days">
        {week.days.map(renderDay)}
      </div>
    );
  }

  const header = (
    <>
      <div className="planner-title" data-testid="planner-title">
        Ukeplan
      </div>
      <div className="planner-hint" data-testid="planner-hint">
        Dra oppskrifter hit for å planlegge uken
      </div>
    </>
  );

  const weekNav = (
    <div className="planner-week-nav">
      <button
        type="button"
        className="planner-nav-btn"
        data-testid="planner-prev-week"
        aria-label="Forrige uke"
        onClick={() => setWeekOffset((o) => o - 1)}
      >
        ‹
      </button>
      <div className="planner-week-label" data-testid="planner-week-label">
        {week.rangeLabel}
      </div>
      <button
        type="button"
        className="planner-nav-btn"
        data-testid="planner-next-week"
        aria-label="Neste uke"
        onClick={() => setWeekOffset((o) => o + 1)}
      >
        ›
      </button>
    </div>
  );

  const shoppingListButton = (
    <button
      type="button"
      className="btn btn-primary planner-shopping-btn"
      data-testid="planner-shopping-list"
      onClick={() => onCreateShoppingList?.(week.monday)}
    >
      Lag handleliste
    </button>
  );

  const mutationErrorBlock = mutationError && (
    <p className="planner-mutation-error" data-testid="planner-mutation-error" role="alert">
      {mutationError}
    </p>
  );

  return (
    <>
      {/* ---- Desktop: collapsible right sidebar ---- */}
      {sidebarOpen ? (
        <aside className="planner-sidebar planner-desktop" data-testid="planner-sidebar">
          <div className="planner-head">
            <div>{header}</div>
            <button
              type="button"
              className="btn btn-ghost planner-collapse-btn"
              data-testid="planner-collapse"
              onClick={() => setSidebarOpen(false)}
            >
              Skjul ›
            </button>
          </div>
          {weekNav}
          {mutationErrorBlock}
          <div className="planner-body">{renderBody()}</div>
          <div className="planner-foot">{shoppingListButton}</div>
        </aside>
      ) : (
        <button
          type="button"
          className="planner-tab planner-desktop"
          data-testid="planner-expand"
          title="Vis ukeplan"
          onClick={() => setSidebarOpen(true)}
        >
          <span aria-hidden="true">‹</span>
          <span className="planner-tab-label">Vis ukeplan</span>
        </button>
      )}

      {/* ---- Mobile: floating button + full-screen overlay ---- */}
      <button
        type="button"
        className="planner-fab planner-mobile"
        data-testid="planner-fab"
        onClick={() => setMobileOpen(true)}
      >
        <span aria-hidden="true">📅</span> Ukeplan
        {state.status === "ok" && state.meals.length > 0 && (
          <span className="planner-fab-dot" aria-hidden="true" data-testid="planner-fab-dot" />
        )}
      </button>

      {mobileOpen && (
        <div className="planner-overlay planner-mobile" data-testid="planner-mobile-overlay">
          <div className="planner-overlay-head">
            <button
              type="button"
              className="planner-overlay-back"
              data-testid="planner-mobile-close"
              aria-label="Lukk ukeplan"
              onClick={() => setMobileOpen(false)}
            >
              ←
            </button>
            <div>{header}</div>
          </div>
          {weekNav}
          {mutationErrorBlock}
          <div className="planner-body">{renderBody()}</div>
          <div className="planner-foot">{shoppingListButton}</div>
        </div>
      )}
    </>
  );
});

/**
 * One day's drop zone (Slice 4). A distinct component — not inlined in the
 * parent's `.map()` — because it calls `useDroppable`, a hook; `renderDay`
 * in `MealPlannerPanel` stays a plain function that returns one of these per
 * day. Disabled (no drop accepted, no state classes) for a read/comment
 * caller (`canEdit=false`) — defense in depth, mirroring every other mutate
 * path in this component; the caller never gives such a member a drag
 * handle to begin with (see `RecipeCard`'s `planDraggable` gating). Exported
 * so the drag-state class contract (`dayDropStateClass`) can be asserted
 * directly at the component level, not just as a pure function — jsdom can't
 * render the visual outline/highlight itself.
 */
export function PlannerDay({
  day,
  meals,
  canEdit,
  dragging,
  renderMeal,
}: {
  day: WeekDay;
  meals: PlannedMeal[];
  canEdit: boolean;
  dragging: boolean;
  renderMeal: (meal: PlannedMeal) => React.JSX.Element;
}): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id: dayDropId(day.date),
    disabled: !canEdit,
  });
  return (
    <div
      ref={setNodeRef}
      className={`planner-day${day.isToday ? " is-today" : ""}${dayDropStateClass(dragging && canEdit, isOver)}`}
      data-testid={`planner-day-${day.date}`}
      data-day-date={day.date}
    >
      <div className="planner-day-head">
        <span className="planner-day-name">{day.label}</span>
        <span className="planner-day-num">{day.date.slice(-2)}.</span>
      </div>
      {meals.length > 0 && (
        <div className="planner-day-meals" data-testid={`planner-day-meals-${day.date}`}>
          {meals.map(renderMeal)}
        </div>
      )}
    </div>
  );
}
